import moment from 'moment';
import React from 'react';
import {
  TextInput,
  PercentInput,
  AddressInput,
  DateInput,
  CurrencyInput,
  CheckboxHeaderCell,
  CheckboxCell,
  CheckboxPrimaryCell,
} from './cells';

export default function useColumns({
  group,
  handleChange,
  selectedRows,
  setSelectedRows,
  allRowIndices,
  isAllSelected,
  isFieldMissing,
  isDuplicateAddress,
  navigation,
}) {
  const wrapWithNavigation = (CellComponent, props, rowIndex, colIndex) => {
    const isActive = navigation.isCellActive && navigation.isCellActive(rowIndex, colIndex);
    const isEditing = navigation.isCellEditing && navigation.isCellEditing(rowIndex, colIndex);

    return (
      <CellComponent
        {...props}
        rowIndex={rowIndex}
        colIndex={colIndex}
        isActive={isActive}
        isEditing={isEditing}
        onStartEditing={() => {
          navigation.setFocusedCell({ rowIndex, colIndex });
          navigation.startEditing();
        }}
        onStopEditing={() => navigation.stopEditing()}
        registerCellRef={navigation.registerCellRef}
      />
    );
  };

  const checkboxColumn = {
    id: 'select',
    header: () => {
      const isActive = navigation.isCellActive && navigation.isCellActive(-1, 0);

      return (
        <CheckboxHeaderCell
          setSelectedRows={setSelectedRows}
          isAllSelected={isAllSelected}
          allRowIndices={allRowIndices}
          isActive={isActive}
          rowIndex={-1}
          colIndex={0}
          registerCellRef={navigation.registerCellRef}
        />
      );
    },
    cell: ({ row }) => {
      const idx = row.index;
      const rowIndex = row.index;
      const colIndex = 0;
      const isNew = row.original._new || false;
      const isPrimary = group === 'primary' && idx === 0;
      const isActive = navigation.isCellActive && navigation.isCellActive(rowIndex, colIndex);

      if (isPrimary) {
        return (
          <CheckboxPrimaryCell
            isActive={isActive}
          />
        );
      }

      return (
        <CheckboxCell
          setSelectedRows={setSelectedRows}
          idx={idx}
          selectedRows={selectedRows}
          disabled={isNew}
          isActive={isActive}
          rowIndex={rowIndex}
          colIndex={colIndex}
          registerCellRef={navigation.registerCellRef}
        />
      );
    },
    size: 40,
    enableResizing: false,
  };
  const basicColumns = [
    {
      header: 'Address',
      accessorKey: 'address',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const fieldName = 'address';
        const colIndex = 1;
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);
        const isDuplicate = isDuplicateAddress && isDuplicateAddress(row.original, fieldName);

        return wrapWithNavigation(
          AddressInput,
          {
            value: getValue() || '',
            onChange: (value) => {
              handleChange(value, 'address', row.original);
            },
            placeholder: 'Enter address...',
            disabled: false,
            isActive: true,
            rowIndex,
            columnIndex: 'address',
            isMissing,
            isDuplicate,
          },
          rowIndex,
          colIndex,
        );
      },
      minSize: 150,
      size: 200,
    },
    {
      header: 'Owner on Title',
      accessorKey: 'titleName',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 2;
        const fieldName = 'titleName';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);

        return wrapWithNavigation(
          TextInput,
          {
            value: getValue(),
            type: 'text',
            onChange: (value) => {
              handleChange(value, 'title_name', row.original);
            },
            initialValues: row.original,
            isMissing,
            rowIndex,
            columnIndex: 'titleName',
          },
          rowIndex,
          colIndex,
        );
      },
      minSize: 150,
    },
    {
      header: '% Ownership',
      accessorKey: 'ownershipPercent',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 3;
        const fieldName = 'ownershipPercent';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);

        return wrapWithNavigation(
          PercentInput,
          {
            value: getValue(),
            type: 'text',
            onChange: (value) => {
              handleChange(value, 'ownership_percent', row.original);
            },
            isMissing,
            rowIndex,
            columnIndex: 'ownershipPercent',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 120,
    },
    {
      header: 'Purchase Date',
      accessorKey: 'purchaseDate',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 4;
        const fieldName = 'purchaseDate';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);
        const date = getValue();

        return wrapWithNavigation(
          DateInput,
          {
            value: date ? moment(date).format('MM/DD/YYYY') : '',
            onChange: (value, isValid) => {
              if (isValid) {
                handleChange(value, 'purchaseDate', row.original, isValid);
              }
            },
            isMissing,
            rowIndex,
            columnIndex: 'purchaseDate',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 140,
    },
  ];

  const sharedColumns = [
    {
      header: 'Property Value',
      accessorKey: 'currentMarketValue',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 5;
        const fieldName = 'currentMarketValue';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);

        return wrapWithNavigation(
          CurrencyInput,
          {
            value: getValue(),
            className: 'right',
            onChange: (value) => {
              handleChange(value, 'current_market_value', row.original);
            },
            isMissing,
            rowIndex,
            columnIndex: 'currentMarketValue',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 140,
    },
    {
      header: 'Lender',
      accessorKey: 'lenderName',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 6;
        const fieldName = 'lenderName';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);

        return wrapWithNavigation(
          TextInput,
          {
            value: getValue(),
            type: 'text',
            className: 'left',
            onChange: (value) => {
              handleChange(value, 'lender_name', row.original);
            },
            isMissing,
            rowIndex,
            columnIndex: 'lenderName',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 140,
    },
    {
      header: 'Original Mortgage Balance',
      accessorKey: 'totalMortgageAmount',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 7;
        const fieldName = 'totalMortgageAmount';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);

        return wrapWithNavigation(
          CurrencyInput,
          {
            value: getValue(),
            onChange: (value) => {
              handleChange(value, 'total_mortgage_amount', row.original);
            },
            isMissing,
            rowIndex,
            columnIndex: 'totalMortgageAmount',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 200,
    },
  ];

  const appFixNFlipsColumns = [
    ...basicColumns,
    {
      header: 'Purchase Price',
      accessorKey: 'purchasePrice',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 5;
        const fieldKey = 'purchasePrice';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldKey);

        return wrapWithNavigation(
          CurrencyInput,
          {
            value: getValue(),
            className: 'right',
            onChange: (value) => {
              handleChange(value, 'purchase_price', row.original);
            },
            isMissing,
            rowIndex,
            columnIndex: 'purchasePrice',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 150,
    },
    {
      header: 'Rehab Budget',
      accessorKey: 'rehabBudget',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 6;
        const fieldKey = 'rehabBudget';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldKey);

        return wrapWithNavigation(
          CurrencyInput,
          {
            value: getValue(),
            className: 'right',
            onChange: (value) => {
              handleChange(value, 'rehab_budget', row.original);
            },
            isMissing,
            rowIndex,
            columnIndex: 'rehabBudget',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 150,
    },
    {
      header: 'Sale Price',
      accessorKey: 'salePrice',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 7;
        const fieldKey = 'salePrice';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldKey);

        return wrapWithNavigation(
          CurrencyInput,
          {
            value: getValue(),
            className: 'right',
            onChange: (value) => {
              handleChange(value, 'sale_price', row.original);
            },
            isMissing,
            rowIndex,
            columnIndex: 'salePrice',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 150,
    },
    {
      header: 'Date Sold',
      accessorKey: 'saleDate',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 8;
        const fieldKey = 'saleDate';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldKey);

        return wrapWithNavigation(
          DateInput,
          {
            value: getValue() ? moment(getValue()).format('MM/DD/YYYY') : '',
            onChange: (value, isValid) => {
              handleChange(value, 'sale_date', row.original, isValid);
            },
            isMissing,
            rowIndex,
            columnIndex: 'saleDate',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 180,
    },
  ];

  const appInvestmentPropertiesOwnedColumns = [
    ...basicColumns,
    ...sharedColumns.filter(col => col.accessorKey !== 'totalMortgageAmount'),
    {
      header: 'Actual Rent',
      accessorKey: 'rentAmount',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 7;
        const fieldName = 'rentAmount';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);

        return wrapWithNavigation(
          CurrencyInput,
          {
            value: getValue(),
            className: 'right',
            onChange: (value) => {
              handleChange(value, 'rent_amount', row.original);
            },
            isMissing,
            rowIndex,
            columnIndex: 'rentAmount',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 200,
    },
    {
      header: 'Market Rent',
      accessorKey: 'marketRent',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 8;
        const fieldName = 'marketRent';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);

        return wrapWithNavigation(
          CurrencyInput,
          {
            value: getValue(),
            className: 'right',
            onChange: (value) => {
              handleChange(value, 'market_rent', row.original);
            },
            isMissing,
            rowIndex,
            columnIndex: 'marketRent',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 200,
    },
  ];

  const appBridgeRentColumns = [
    ...basicColumns,
    {
      header: 'Purchase Price',
      accessorKey: 'purchasePrice',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 5;
        const fieldKey = 'purchasePrice';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldKey);

        return wrapWithNavigation(
          CurrencyInput,
          {
            value: getValue(),
            className: 'right',
            onChange: (value) => {
              handleChange(value, 'purchase_price', row.original);
            },
            isMissing,
            rowIndex,
            columnIndex: 'purchasePrice',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 150,
    },
    {
      header: 'Rehab Budget',
      accessorKey: 'rehabBudget',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 6;
        const fieldKey = 'rehabBudget';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldKey);

        return wrapWithNavigation(
          CurrencyInput,
          {
            value: getValue(),
            className: 'right',
            onChange: (value) => {
              handleChange(value, 'rehab_budget', row.original);
            },
            isMissing,
            rowIndex,
            columnIndex: 'rehabBudget',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 150,
    },
    {
      header: 'Rent Amount',
      accessorKey: 'rentAmount',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const colIndex = 7;
        const fieldKey = 'rentAmount';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldKey);

        return wrapWithNavigation(
          CurrencyInput,
          {
            value: getValue(),
            className: 'right',
            onChange: (value) => {
              handleChange(value, 'rent_amount', row.original);
            },
            isMissing,
            rowIndex,
            columnIndex: 'rentAmount',
          },
          rowIndex,
          colIndex,
        );
      },
      size: 150,
    },
  ];

  const appPrimaryResidenceAndSecondHomesColumns = [
    ...basicColumns,
    ...sharedColumns
  ];

  if (group === 'bridge') {
    return [checkboxColumn, ...appFixNFlipsColumns];
  } else if (group === 'bridge-rent') {
    return [checkboxColumn, ...appBridgeRentColumns];
  } else if (group === 'rental') {
    return [checkboxColumn, ...appInvestmentPropertiesOwnedColumns];
  } else if (group === 'primary') {
    return [checkboxColumn, ...appPrimaryResidenceAndSecondHomesColumns];
  } else if (group === 'new_construction') {
    return [checkboxColumn, ...appFixNFlipsColumns];
  }


  return [];
}
